'use client'

import { Footer as FooterType } from '@/payload-types'
import { Language } from '@/types/language.types'
import { ArrowRight, Facebook, Instagram, Linkedin } from 'lucide-react'
import { Link } from '@/i18n/navigation'
import FirstLogo from '@/public/logo/first.svg'
import { memo } from 'react'
import LanguageSwitcher from './LanguageSwitcher'
import { MenuLinkWrapper } from '../common/LinkWrapper'
import { useTranslations } from 'next-intl'

const MemoizedLanguageSwitcher = memo(LanguageSwitcher)

const Footer = ({ footer, language }: { footer: FooterType; language: Language }) => {
  const t = useTranslations()

  return (
    <footer className='mt-12 z-40 relative'>
      <div className='bg-stone-900 w-full'>
        <div className='container-max mx-auto px-4 py-8 space-y-8 flex flex-col w-full md:grid md:grid-cols-12 md:items-baseline'>
          <div className='[&>*:not(:last-child)]:border-b [&>*:first-child]:pt-2 [&>*:not(:last-child)]:border-b-stone-800 md:col-start-1 md:col-end-6'>
            {footer.navItems &&
              footer.navItems.length > 0 &&
              footer.navItems.map((navItem, navIndex) => (
                <MenuLinkWrapper
                  key={navItem.id + '-' + navIndex}
                  link={navItem.link}
                  className='text-stone-100 text-xl flex justify-between py-4'
                >
                  <p>{navItem.link.label}</p>

                  <ArrowRight className='size-6 text-muted-foreground' />
                </MenuLinkWrapper>
              ))}
          </div>

          <div className='flex flex-col space-y-4 md:col-start-7 md:col-end-12 pt-2'>
            {footer.navItemsSmall &&
              footer.navItemsSmall.length > 0 &&
              footer.navItemsSmall.map((navItem, navIndex) => (
                <MenuLinkWrapper
                  key={navItem.id + '-' + navIndex}
                  link={navItem.link}
                  className='text-stone-300 '
                >
                  {navItem.link.label}
                </MenuLinkWrapper>
              ))}
          </div>

          <div className='w-full justify-center items-center flex md:justify-end-end md:col-span-1'>
            <MemoizedLanguageSwitcher currentLocale={language} />
          </div>
        </div>
      </div>

      {/* BOTTOM FOOTER */}
      <div className='bg-stone-950'>
        <div className='container-max mx-auto px-4 py-8'>
          <div className='flex justify-between items-center pb-4 border-b-stone-700 border-b'>
            <Link href='/' aria-label={`First Hotels – ${t('home')}`}>
              <FirstLogo className='w-24 [&>*]:fill-white' />
            </Link>

            <div className='flex space-x-4'>
              <a
                href='https://www.facebook.com/FirstHotels'
                target='_blank'
                rel='noopener noreferrer'
                aria-label='Facebook'
              >
                <Facebook className='text-stone-500' strokeWidth={1.5} aria-hidden='true' />
              </a>
              <a
                href='https://www.linkedin.com/company/22596/'
                target='_blank'
                rel='noopener noreferrer'
                aria-label='LinkedIn'
              >
                <Linkedin className='text-stone-500' strokeWidth={1.5} aria-hidden='true' />
              </a>
              <a
                href='https://www.instagram.com/first.hotels.official/'
                target='_blank'
                rel='noopener noreferrer'
                aria-label='Instagram'
              >
                <Instagram className='text-stone-500' strokeWidth={1.5} aria-hidden='true' />
              </a>
            </div>
          </div>

          <div className='pt-6 flex flex-col space-y-4 '>
            <div className='flex space-x-4 text-stone-400 items-center flex-wrap'>
              <Link href={'/privacy-policy'}>{t('privacy_policy')}</Link>
              <span className='size-1.5 bg-muted-foreground rounded-full' />
              <Link href={'/booking-conditions'}>{t('terms_and_conditions')}</Link>
              <span className='size-1.5 bg-muted-foreground rounded-full' />
              <Link href={'/cookies'}>{t('cookies')}</Link>
            </div>

            <p className='text-stone-200'>{`© ${new Date().getFullYear()} FIRST HOTELS`}</p>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
